import { useContext, useMemo } from 'react';

import type { UIFeatures } from '../../types';
import UIFeaturesContext from '../context';

type UseUIFeaturesMapCallback<T> = (features: UIFeatures) => T;

interface UseUIFeatures {
  (): UIFeatures;
  <T>(mapCallback: UseUIFeaturesMapCallback<T>): T;
}

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const useUIFeatures: UseUIFeatures = <T>(mapCallback?: UseUIFeaturesMapCallback<T>) => {
  const features = useContext(UIFeaturesContext);

  return useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => {
      const featuresObj = features ?? {};

      return mapCallback ? mapCallback(featuresObj) : featuresObj;
    },
    // `mapCallback` is intentionally excluded from the dependency array. It is meant to be invoked only when `features` changes.
    // oxlint-disable-next-line react/exhaustive-deps
    [features]
  );
};
